import type { DailyBite } from '@nelnela/contracts';

/**
 * "Play all · {goal} min" (design `2b`) — the pure part of running the day's tiles back to back.
 *
 * The pill is the one control on Today that says "just take me through it", and everything
 * awkward about it is a question of what counts as done. A tile the learner dismissed is not in
 * the run. A tile they already finished this morning is not in the run. A tile they abandon
 * half-way stops the run rather than skipping past it — being marched on to the next thing after
 * giving up on this one is the opposite of what the pill promised.
 *
 * The queue holds plan INDICES, not tiles: the plan is refetched between activities (each one
 * ends by writing progress back into `daily_bite.plan`), so a captured tile object would be a
 * stale copy of a row that has just changed underneath it.
 */

export interface PlayAllState {
  /** plan indices still to run, in plan order */
  queue: number[];
  /** the index currently open, or null between tiles and when the run is over */
  current: number | null;
  status: 'idle' | 'running' | 'done';
}

export const IDLE: PlayAllState = { queue: [], current: null, status: 'idle' };

/** Is this tile something Play all should open? */
export function isRunnable(tile: DailyBite['plan'][number]): boolean {
  if (tile.dismissed) return false;
  if (tile.completedActivityId !== null) return false;
  return true;
}

/**
 * Build the run from the plan as it stands right now.
 *
 * Nothing runnable is not an error and not an empty run — it is a finished day, and the caller
 * shows the evening state rather than a pill that does nothing.
 */
export function startPlayAll(plan: DailyBite['plan']): PlayAllState {
  const queue = plan.filter(isRunnable).map((t) => t.idx);
  if (queue.length === 0) return { queue: [], current: null, status: 'done' };
  return { queue: queue.slice(1), current: queue[0]!, status: 'running' };
}

/**
 * The learner finished the tile that was open. Moves to the next.
 *
 * `completed` is what the tile's activity reported, not what the plan says: the plan is refetched
 * after this, and waiting for that round trip before opening the next tile would put a blank
 * screen between every pair of activities.
 */
export function advancePlayAll(s: PlayAllState): PlayAllState {
  if (s.status !== 'running') return s;
  const [next, ...rest] = s.queue;
  if (next === undefined) return { queue: [], current: null, status: 'done' };
  return { queue: rest, current: next, status: 'running' };
}

/**
 * The learner left a tile early, or pressed the back button, or dismissed the run.
 *
 * The whole run stops. Continuing to the next tile after someone has just abandoned one would
 * read as the app not listening — and the tiles are all still there on Today, in order, for
 * whenever they want the next one.
 */
export function stopPlayAll(): PlayAllState {
  return IDLE;
}

/** Is Play all worth showing at all? Hidden when the plan is empty or the day is finished. */
export function canPlayAll(plan: DailyBite['plan']): boolean {
  return plan.some(isRunnable);
}

/**
 * Where a tile opens.
 *
 * Every route carries `dailyBiteId` and `planIdx` so the activity it starts can be attributed
 * back to the tile — that pair is what the end handler uses to mark the tile complete, and a
 * tile opened without them records the minutes but never flips.
 *
 * The watch and listen tiles carry their SPAN, and watch additionally asks the player to loop it:
 * the bite's promise is eight minutes of this scene, not the rest of the episode.
 */
export function tileHref(bite: DailyBite, tile: DailyBite['plan'][number]): string | null {
  const t = tile.target ?? {};
  const bid = bite.id;
  const q = (extra: Record<string, string | number | undefined>): string => {
    const p = new URLSearchParams({ dailyBiteId: bid, planIdx: String(tile.idx) });
    for (const [k, v] of Object.entries(extra)) if (v !== undefined) p.set(k, String(v));
    return p.toString();
  };

  switch (tile.activityType) {
    case 'watch':
      if (!t.videoId) return null;
      return `/watch/${t.videoId}?${q({ t: t.startMs, end: t.endMs, loop: t.endMs ? 1 : undefined })}`;
    case 'listen':
      if (!t.videoId) return null;
      return `/listen?${q({ video: t.videoId, startMs: t.startMs, endMs: t.endMs })}`;
    case 'story':
      // A read tile with no story yet is the normal case: the story is written on tap, on /read,
      // which owns the generating state. Composing one would have blocked the Today request.
      return t.storyId ? `/read/${t.storyId}?${q({})}` : `/read?${q({})}`;
    case 'culture':
      // M6b (`3d`). Its own case rather than sharing 'story': a culture note without a storyId is
      // nothing at all — there is no "write me one" fallback for a curated note — so falling
      // through to `/read` would offer the story generator under a culture heading.
      return t.storyId ? `/read/${t.storyId}?${q({})}` : null;
    case 'lesson':
      return t.lessonId ? `/words/lesson/${t.lessonId}?${q({})}` : null;
    case 'review':
      return `/review?${q({
        exerciseIds: t.exerciseIds?.length ? t.exerciseIds.join(',') : undefined,
        itemIds: t.exerciseIds?.length ? undefined : tile.itemIds.join(',') || undefined,
      })}`;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------------------------
// surviving the navigation
// ---------------------------------------------------------------------------------------------

/**
 * A run has to outlive Today.
 *
 * Every tile is a real screen — Watch, Review, the reader — so running the day's plan means
 * navigating away and back four times, and Today unmounts each time. A queue held in component
 * state would open exactly one tile and then forget it had ever been asked.
 *
 * `sessionStorage`, not `localStorage`: a run belongs to this tab and this sitting. Two tabs
 * running the same day would fight over which tile is next, and a run resumed a week later would
 * be nonsense. The bite id is stored with it for the same reason — a run from yesterday's plan
 * must not drive today's, and the ids differ per day.
 */
const RUN_KEY = 'nn.today.playAll';

interface StoredRun {
  biteId: string;
  state: PlayAllState;
}

export function saveRun(biteId: string, state: PlayAllState): void {
  try {
    if (state.status !== 'running') sessionStorage.removeItem(RUN_KEY);
    else sessionStorage.setItem(RUN_KEY, JSON.stringify({ biteId, state } satisfies StoredRun));
  } catch {
    // Private mode, or a full quota. The run then simply does not survive the navigation, which
    // is the pre-M6a behaviour and is much better than a screen that will not render.
  }
}

/** The stored run, if it is for THIS bite and still running. Anything else is cleared. */
export function loadRun(biteId: string): PlayAllState | null {
  try {
    const raw = sessionStorage.getItem(RUN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRun;
    if (parsed?.biteId !== biteId || parsed.state?.status !== 'running') {
      sessionStorage.removeItem(RUN_KEY);
      return null;
    }
    return parsed.state;
  } catch {
    return null;
  }
}

export function clearRun(): void {
  try {
    sessionStorage.removeItem(RUN_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Coming back to Today mid-run: is the tile we opened actually finished?
 *
 * Only advance when it is. Someone who opened Watch, watched a minute and came back has not
 * finished the tile, and marching them on to the review would be the app deciding they had.
 */
export function shouldAdvance(state: PlayAllState, plan: DailyBite['plan']): boolean {
  if (state.status !== 'running' || state.current === null) return false;
  const tile = plan.find((t) => t.idx === state.current);
  if (!tile) return false;
  return tile.completedActivityId !== null || tile.dismissed;
}

/**
 * The bite's grip on an activity, read off a tile link's query string.
 *
 * `tileHref` writes `dailyBiteId` and `planIdx` onto every tile link, and `activityEnd` uses that
 * pair to find the tile and mark it complete. A screen that opens from a tile and does not send
 * them back records the minutes but never flips the tile — and because `shouldAdvance` waits for
 * `completedActivityId`, a "Play all" run that opens such a tile never advances past it.
 *
 * That is exactly what happened: only Listen read these. Watch and Read called `startActivity`
 * with a target and no scope, so their tiles could never complete, and a run whose first tile was
 * either of them stalled on tile 0. The rule now lives here, once, rather than inlined in each
 * screen — three copies is how two of them came to be missing.
 *
 * Returned ready to spread into `startActivity`'s deps: the pair goes together or not at all,
 * because a `planIdx` with no bite points at nothing. `planIdx` is parsed like a span bound —
 * finite or absent — since a NaN from a mangled link serialises to `null`, which the route
 * rejects, costing the whole activity rather than just the bite's grip. Tile 0 is kept: `'0'` is
 * a truthy string and `Number('0')` is 0.
 */
export function biteScopeFrom(params: {
  get(key: string): string | null;
}): { dailyBiteId: string; planIdx: number } | Record<string, never> {
  const dailyBiteId = params.get('dailyBiteId');
  const raw = params.get('planIdx');
  if (!dailyBiteId || raw === null || raw.trim() === '') return {};
  const planIdx = Number(raw);
  if (!Number.isFinite(planIdx) || !Number.isInteger(planIdx) || planIdx < 0) return {};
  return { dailyBiteId, planIdx };
}
